import dayjs from 'dayjs';
import dayjscalendar from 'dayjs/plugin/calendar';
import dayjslocalizedFormat from 'dayjs/plugin/localizedFormat';
import dayjstimezone from 'dayjs/plugin/timezone';
import dayjsutc from 'dayjs/plugin/utc';

dayjs.extend(dayjscalendar);
dayjs.extend(dayjslocalizedFormat);
dayjs.extend(dayjstimezone);
dayjs.extend(dayjsutc);

export function doesDateStringContainOffset(date: string): boolean {
	return date.endsWith('Z') || (/[+-]\d\d:\d\d$/u).test(date);
}

export function getLocalDateTime(date: string): dayjs.Dayjs {
	return doesDateStringContainOffset(date) ? dayjs(date) : dayjs(`${date}+00:00`).utc(true);
}

export function getDate(date: string): dayjs.Dayjs {
	const trimmedDate = date.includes('T') ? date.slice(0, date.indexOf('T')) : date;
	const utcDate = dayjs.utc(trimmedDate);

	return utcDate;
}

export function getCalendarDateTime(date: string): string {
	return getLocalDateTime(date).format('YYYY-MM-DD h:mma');
}

export function getShortDate(date: string): string {
	return getLocalDateTime(date).format('M/D/YY');
}

export function getTimeRecordDate(date: string): string {
	return getLocalDateTime(date).format('M/D/YY h:mm a');
}

export function convertISODateToHumanReadableDate(date: string): string {
	return getLocalDateTime(date).format('dddd, MMMM Do YYYY, h:mm:ss a');
}

export function convertISODateToShortDate(date: string): string {
	return getLocalDateTime(date).format('MMM D YYYY, h:mm:ss a');
}

export function convertISODateToEventDate(date: string): string {
	return getLocalDateTime(date).format('M/D/YYYY, h:mm:ss A z');
}

export function convertISODateToPardotDate(date: string): string {
	return getLocalDateTime(date).format('MMM D, YYYY h:mm a');
}

export function getPastDate(daysAgo: number): string {
	return dayjs().startOf('day')
		.subtract(daysAgo, 'day')
		.format('YYYY-MM-DD');
}

export function formatUtcDateTime(date: string): string {
	return dayjs.utc(date).format('MMMM D, YYYY [at] h:mm A [UTC]');
}

type RelativeTime =
	| { unit: 'now' }
	| { count: number; unit: 'day' | 'hour' | 'minute' }
	| { label: string; unit: 'date' };

const SHORT_UNITS = {
	day: 'd',
	hour: 'h',
	minute: 'm'
};

function getRelativeTime(value: string | Date, now: Date): RelativeTime | null {
	const date = dayjs.utc(value).local();
	if (!date.isValid()) {
		return null;
	}

	const current = dayjs(now);

	if (current.diff(date, 'second') < 60) {
		return { unit: 'now' };
	}

	const minutes = current.diff(date, 'minute');
	if (minutes < 60) {
		return {
			count: minutes,
			unit: 'minute'
		};
	}

	const hours = current.diff(date, 'hour');
	if (hours < 24) {
		return {
			count: hours,
			unit: 'hour'
		};
	}

	const days = current.startOf('day').diff(date.startOf('day'), 'day');
	if (days < 7) {
		return {
			count: Math.max(1, days),
			unit: 'day'
		};
	}

	return {
		label: date.format(date.year() === current.year() ? 'MMM D' : 'MMM D, YYYY'),
		unit: 'date'
	};
}

export function formatRelativeTime(value: string | Date, now: Date = new Date()): string {
	const relative = getRelativeTime(value, now);

	if (!relative) {
		return '';
	}
	if (relative.unit === 'now') {
		return 'now';
	}
	if (relative.unit === 'date') {
		return relative.label;
	}

	return `${relative.count}${SHORT_UNITS[ relative.unit ]}`;
}

export function describeRelativeTime(value: string | Date, now: Date = new Date()): string {
	const relative = getRelativeTime(value, now);

	if (!relative) {
		return '';
	}
	if (relative.unit === 'now') {
		return 'just now';
	}
	if (relative.unit === 'date') {
		return relative.label;
	}

	return `${relative.count} ${relative.unit}${relative.count === 1 ? '' : 's'} ago`;
}
